import { Fragment, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { LORAX, KOALA, GREAT_WHITE } from 'HubStyleTokens/colors';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import Big from 'UIComponents/elements/Big';
import UITable from 'UIComponents/table/UITable';
import UISection from 'UIComponents/section/UISection';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILink from 'UIComponents/link/UILink';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import * as Sync from '../constants/dataSync/Sync';
import DataSyncCard from './DataSyncCard';
import { getDataSyncProductUrl } from '../../common/utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SYNC_DIRECTION_ICON_SIZE = 24;
const EmptyComponent = () => {
  return null;
};
const DataSyncBadge = styled(UIBadge).withConfig({
  displayName: "SharedData__DataSyncBadge",
  componentId: "sc-1qht815-0"
})(["background-color:", ";height:18px;margin-bottom:20px;&:hover{background-color:", ";}"], KOALA, GREAT_WHITE);
const SyncDirectionIcon = ({
  sync
}) => {
  let iconName;
  switch (sync) {
    case Sync.INBOUND:
      iconName = 'dataSyncRight';
      break;
    case Sync.OUTBOUND:
      iconName = 'dataSyncLeft';
      break;
    case Sync.BOTH:
      iconName = 'dataSync';
      break;
    default:
  }
  if (iconName) {
    return /*#__PURE__*/_jsx(UIIcon, {
      name: iconName,
      color: LORAX,
      size: SYNC_DIRECTION_ICON_SIZE
    });
  } else {
    return null;
  }
};
function SharedData({
  dataSyncHowItWorks,
  dataSyncInfo,
  dataSyncs,
  iconUrl,
  name,
  onTrackInteraction,
  opsHub
}) {
  const trackInteraction = useCallback(action => onTrackInteraction(action, 'overview'), [onTrackInteraction]);
  const renderedSharedDataRows = useMemo(() => dataSyncs.map((dataSync, index) => {
    return /*#__PURE__*/_jsxs("tr", {
      children: [/*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(Big, {
          style: {
            fontWeight: 500
          },
          children: dataSync.target
        })
      }), /*#__PURE__*/_jsx(UITooltip, {
        onOpenChange: ({
          target: {
            value: open
          }
        }) => {
          if (open) {
            trackInteraction('hover shared data description');
          }
        },
        placement: "top",
        title: dataSync.description || /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-apps-core.detail.overview.sharedData.dataFlow${dataSync.sync}`,
          options: {
            name
          }
        }),
        children: /*#__PURE__*/_jsx("td", {
          className: "text-center",
          children: /*#__PURE__*/_jsx(SyncDirectionIcon, {
            sync: dataSync.sync
          })
        })
      }, `shared-data-${index}`), /*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(Big, {
          style: {
            fontWeight: 500
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-apps-core.dataSyncs.data.${dataSync.data}`
          })
        })
      })]
    }, `shared-data-${index}`);
  }), [dataSyncs, trackInteraction, name]);
  const renderedDataSyncInfo = dataSyncInfo && !opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("b", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-ui-apps-core.detail.overview.sharedData.moreInfoText`
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(UIBreakString, {
        children: dataSyncInfo
      })
    })]
  }) : null;
  const renderedHowItWorks = dataSyncHowItWorks && opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("p", {
      className: "m-bottom-1",
      children: /*#__PURE__*/_jsx("b", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-apps-core.detail.overview.sharedData.howItWorks`
        })
      })
    }), /*#__PURE__*/_jsx(UIBreakString, {
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: dataSyncHowItWorks
      })
    })]
  }) : null;
  const renderedDataSyncs = useMemo(() => {
    if (opsHub) {
      return /*#__PURE__*/_jsx("div", {
        children: dataSyncs.map((dataSync, index) => /*#__PURE__*/_jsx(DataSyncCard, {
          dataSync: dataSync,
          iconUrl: iconUrl,
          onToggleOpen: () => trackInteraction('toggle field mapping section open'),
          SyncIcon: SyncDirectionIcon
        }, `data-sync-card-${index}`))
      });
    } else {
      return /*#__PURE__*/_jsxs(UITable, {
        hover: false,
        children: [/*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `marketplace-ui-apps-core.detail.overview.sharedData.appData`,
                options: {
                  name
                }
              })
            }), /*#__PURE__*/_jsx("th", {
              className: "text-center",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `marketplace-ui-apps-core.detail.overview.sharedData.dataFlow`
              })
            }), /*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `marketplace-ui-apps-core.detail.overview.sharedData.hubSpotData`
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: renderedSharedDataRows
        })]
      });
    }
  }, [opsHub, dataSyncs, iconUrl, name, renderedSharedDataRows, trackInteraction]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [opsHub && /*#__PURE__*/_jsx(UILink, {
      href: getDataSyncProductUrl(getLang()),
      onClick: () => trackInteraction('click data sync badge'),
      external: true,
      ExternalIcon: EmptyComponent,
      children: /*#__PURE__*/_jsx(DataSyncBadge, {
        use: "koala",
        children: /*#__PURE__*/_jsx(UIMediaObject, {
          itemLeft: /*#__PURE__*/_jsx(UIIcon, {
            name: "dataSync",
            size: "xxs",
            color: LORAX,
            style: {
              marginTop: 1
            }
          }),
          spacing: "none",
          style: {
            marginTop: 1
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-apps-core.detail.overview.sharedData.badge`
          })
        })
      })
    }), renderedHowItWorks, /*#__PURE__*/_jsx(UISection, {
      children: renderedDataSyncs
    }), renderedDataSyncInfo]
  });
}
export default SharedData;